import { useCallback, useRef } from "react";
import { useCart } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import {
    flattenMaterialSelectionsForSync,
    materialQuantitiesDiffer,
    normalizeMaterialQuantity,
} from "./utils/materialLineHelpers";

/**
 * useDatabaseSync
 * ---------------
 * Compares UI state vs DB state and syncs the differences.
 * Supports single-line categories and multi-line categories (e.g. furnace by size).
 */
export function useDatabaseSync() {
    const { project } = useProject();
    const { useCreate, useUpdate, useDelete } = useCart();

    const createSvc = useCreate();
    const updateSvc = useUpdate();
    const deleteSvc = useDelete();

    const dbStateRef = useRef<Record<string, any>>({});
    const syncInFlightRef = useRef(false);

    const initializeDbState = useCallback((dataFromDatabase: Record<string, any>) => {
        dbStateRef.current = flattenMaterialSelectionsForSync(dataFromDatabase);
    }, []);

    const syncToDatabase = useCallback(
        async (uiState: Record<string, any>) => {
            const projectId = project?.id;
            if (!projectId || syncInFlightRef.current) return;

            syncInFlightRef.current = true;
            try {
                const prevFlat = dbStateRef.current;
                const nextFlat = flattenMaterialSelectionsForSync(uiState, prevFlat);

                const toCreate: any[] = [];
                const toUpdate: any[] = [];
                const deleteIdSet = new Set<string>();

                Object.entries(nextFlat).forEach(([syncKey, uiItem]: [string, any]) => {
                    const dbItem = prevFlat[syncKey];
                    const resolvedId = uiItem.id || dbItem?.id;
                    const isComplete =
                        !!uiItem.value &&
                        !!uiItem.manufacturerProductId &&
                        normalizeMaterialQuantity(uiItem.quantity) > 0 &&
                        !uiItem.awaitingColor;

                    if (resolvedId && !isComplete) {
                        deleteIdSet.add(resolvedId);
                        return;
                    }

                    if (!isComplete) return;

                    if (!resolvedId) {
                        toCreate.push({ syncKey, ...uiItem });
                    } else if (uiItem.manufacturerProductId !== dbItem?.manufacturerProductId) {
                        deleteIdSet.add(resolvedId);
                        toCreate.push({ syncKey, ...uiItem });
                    } else if (materialQuantitiesDiffer(uiItem.quantity, dbItem?.quantity)) {
                        toUpdate.push({ id: resolvedId, syncKey, quantity: normalizeMaterialQuantity(uiItem.quantity) });
                    }
                });

                Object.keys(prevFlat).forEach((syncKey) => {
                    if (!nextFlat[syncKey] && prevFlat[syncKey]?.id) {
                        deleteIdSet.add(prevFlat[syncKey].id);
                    }
                });

                toUpdate.forEach((item) => deleteIdSet.delete(item.id));

                const toDelete = Array.from(deleteIdSet);
                const hasNoChanges = toCreate.length === 0 && toUpdate.length === 0 && toDelete.length === 0;
                if (hasNoChanges) return;

                dbStateRef.current = nextFlat;

                for (const id of toDelete) {
                    if (!id || id.startsWith?.("pending_")) continue;
                    try {
                        await deleteSvc.mutateAsync({ Id: id });
                    } catch (err: any) {
                        if (err?.response?.status !== 404) {
                            console.error(`[DatabaseSync] DELETE failed for id ${id}:`, err);
                        }
                    }
                }

                if (toCreate.length > 0) {
                    try {
                        const createdItems: any[] = await createSvc.mutateAsync({
                            ProjectId: projectId,
                            CartItems: toCreate.map((item) => ({
                                ManufacturerProductId: item.manufacturerProductId,
                                ItemCount: normalizeMaterialQuantity(item.quantity),
                                ProductCategory: item.category,
                            })),
                        });

                        createdItems?.forEach((created: any, index: number) => {
                            const source = toCreate[index];
                            if (!source?.syncKey || !created?.id) return;
                            dbStateRef.current[source.syncKey] = {
                                ...dbStateRef.current[source.syncKey],
                                id: created.id,
                            };
                        });
                    } catch (err) {
                        console.error("[DatabaseSync] CREATE failed:", err);
                    }
                }

                for (const item of toUpdate) {
                    try {
                        await updateSvc.mutateAsync({
                            Id: item.id,
                            ItemCount: normalizeMaterialQuantity(item.quantity),
                        });
                    } catch (err) {
                        console.error(`[DatabaseSync] UPDATE failed for id ${item.id}:`, err);
                    }
                }
            } finally {
                syncInFlightRef.current = false;
            }
        },
        [project?.id, createSvc, updateSvc, deleteSvc],
    );

    return {
        initializeDbState,
        syncToDatabase,
    };
}
